import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", product_type: "", country: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir votre nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Demande envoyée ! Je vous recontacte très vite.");
      const wa = waLink(
        `Bonjour King of Sourcing,%0AJe m'appelle ${form.name}.%0AType de produit : ${form.product_type || "-"}%0APays : ${form.country || "-"}%0A${form.message}`
      );
      window.open(wa, "_blank");
      setForm({ name: "", email: "", product_type: "", country: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-red mb-4">Devis gratuit</p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-none text-brand-navy text-4xl sm:text-5xl">
            Parlez-moi de votre projet
          </h2>
          <p className="mt-5 text-slate-600 text-base lg:text-lg max-w-lg">
            Décrivez le produit que vous recherchez. Je vous réponds rapidement avec une estimation
            personnalisée. Réponse immédiate possible via WhatsApp.
          </p>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quote-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-whatsapp px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:-translate-y-1 transition-transform duration-300"
          >
            <MessageCircle size={18} /> Contact direct WhatsApp
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="quote-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-lg bg-white border border-black/5 p-7 sm:p-9 shadow-sm space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet *</Label>
              <Input id="name" data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Votre nom" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" data-testid="quote-email" value={form.email} onChange={set("email")} placeholder="vous@email.com" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Type de produit</Label>
              <Input data-testid="quote-product" value={form.product_type} onChange={set("product_type")} placeholder="Électronique, mode, machines…" />
            </div>
            <div className="space-y-2">
              <Label>Pays de sourcing</Label>
              <Select value={form.country} onValueChange={set("country")}>
                <SelectTrigger data-testid="quote-country"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Chine">Chine</SelectItem>
                  <SelectItem value="Corée">Corée</SelectItem>
                  <SelectItem value="Chine & Corée">Chine & Corée</SelectItem>
                  <SelectItem value="Je ne sais pas">Je ne sais pas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Votre demande *</Label>
            <Textarea id="message" data-testid="quote-message" rows={4} value={form.message} onChange={set("message")} placeholder="Décrivez le produit, la quantité, le budget…" />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Envoi…" : "Envoyer ma demande"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
